import { produce } from "immer";

import DetailedElement from "../../../models/domain/detailed_element";
import PlannedBuildingElementConverter from "../../converters/planned_building_element_converter";
import { DETAILED_ELEMENTS_LOADED, DetailedElementsLoadedEvent } from "../../../events/loaded/building_elements/detailed_elements_loaded";
import { DEVIATION_MARKED_AS_NOT_RESOLVED, DeviationMarkedAsNotResolvedEvent } from "../../../events/viewer/deviation_marked_as_not_resolved";
import { DEVIATION_MARKED_AS_RESOLVED, DeviationMarkedAsResolvedEvent } from "../../../events/viewer/deviation_marked_as_resolved";
import { ELEMENT_DETAILS_LOADED, ElementDetailsLoadedEvent } from "../../../events/loaded/building_elements/element_details_loaded";
import { ELEMENTS_EDITED } from "../../../events/viewer/elements_edited";
import { ELEMENTS_EXCLUDE_FROM_ANALYSIS_UPDATED } from "../../../events/viewer/elements_exclude_from_analysis_updated";
import { ELEMENTS_PARTIAL_PROGRESS_UPDATED } from "../../../events/viewer/elements_partial_progress_updated";
import { ELEMENTS_STATUSES_UPDATED } from "../../../events/viewer/elements_statuses_updated";
import { ELEMENTS_VERIFIED_UPDATED, ElementsVerifiedUpdatedEvent } from "../../../events/viewer/elements_verified_updated";
import { EXPORTED_TO_BCF, ExportedToBcfEvent } from "../../../events/viewer/exported_to_bcf";
import { MINIMAL_PLANNED_BUILDING_ELEMENTS_LOADED, MinimalPlannedBuildingElementsLoadedEvent } from "../../../events/loaded/building_elements/minimal_planned_building_elements_loaded";
import { PLANNED_BUILDING_ELEMENTS_LOADED, PlannedBuildingElementsLoadedEvent } from "../../../events/loaded/building_elements/planned_building_elements_loaded";
import { PUSH_TO_BIM_UNDONE, PushToBimUndoneEvent } from "../../../events/viewer/push_to_bim_undone";
import { PUSHED_ALL_TO_BIM, PushedAllToBimEvent } from "../../../events/viewer/pushed_all_to_bim";
import { PUSHED_TO_BIM, PushedToBimEvent } from "../../../events/viewer/pushed_to_bim";
import { SELECTED_ELEMENTS_LOADED, SelectedElementsLoadedEvent } from "../../../events/loaded/building_elements/selected_elements_loaded";
import { isBigBimViewerPerformanceOn } from "../../utilities/big_bim_viewer_performance";

import type { ByFirebaseId } from "type_aliases";
import type { ElementEditedEvent } from "../../../models/domain/element_edited_event";
import type { PlannedBuildingElementArg } from "../../../models/domain/planned_building_element";
import type { Reducer } from "redux";

type ElementDetailsEvents =
  | DetailedElementsLoadedEvent
  | DeviationMarkedAsNotResolvedEvent
  | DeviationMarkedAsResolvedEvent
  | ElementDetailsLoadedEvent
  | ElementEditedEvent
  | ElementsVerifiedUpdatedEvent
  | ExportedToBcfEvent
  | MinimalPlannedBuildingElementsLoadedEvent
  | PlannedBuildingElementsLoadedEvent
  | PushedAllToBimEvent
  | PushedToBimEvent
  | PushToBimUndoneEvent
  | SelectedElementsLoadedEvent;

/**
 * Details loaded for individual elements (eg: on selection, or for a box selection). Kept out of plannedBuildingElements so that loading
 * them doesn't replace the floor's element map, which reruns every selector over the floor. Read an element
 * with its details through getPlannedElementWithDetails.
 */
export type ElementDetailsStore = {
  byFirebaseFloorId: ByFirebaseId<{ byGlobalId: { [globalId: string]: PlannedBuildingElementArg } }>
};

const reduceElementDetails: Reducer<ElementDetailsStore, ElementDetailsEvents> = (elementDetails: ElementDetailsStore = { byFirebaseFloorId: {} }, event) => {
  switch (event.type) {
    case ELEMENT_DETAILS_LOADED: {
      // Before bigBimViewerPerformance these went into the floor's elements (see reduce_planned_building_elements).
      if (!isBigBimViewerPerformanceOn()) {
        return elementDetails;
      }
      return produce(elementDetails, (draft) => {
        const { floorId, elements } = event.payload;
        draft.byFirebaseFloorId[floorId] ||= { byGlobalId: {} };
        for (const element of elements) {
          draft.byFirebaseFloorId[floorId].byGlobalId[element.globalId] = {
            ...PlannedBuildingElementConverter.detailedElementToPlannedBuildingElement(new DetailedElement(element)),
            loaded: true
          };
        }
      });
    }
    case SELECTED_ELEMENTS_LOADED: {
      return produce(elementDetails, (draft) => {
        const { floorId, elements } = event.payload;
        draft.byFirebaseFloorId[floorId] ||= { byGlobalId: {} };
        for (const element of elements) {
          draft.byFirebaseFloorId[floorId].byGlobalId[element.globalId] = { ...element, loaded: true } as PlannedBuildingElementArg;
        }
      });
    }
    // These edit or reload the floor's elements in plannedBuildingElements. Drop the floor's cached details so
    // they can't mask the change; they're loaded again on the next selection.
    case DETAILED_ELEMENTS_LOADED:
    case DEVIATION_MARKED_AS_NOT_RESOLVED:
    case DEVIATION_MARKED_AS_RESOLVED:
    case ELEMENTS_EDITED:
    case ELEMENTS_EXCLUDE_FROM_ANALYSIS_UPDATED:
    case ELEMENTS_PARTIAL_PROGRESS_UPDATED:
    case ELEMENTS_STATUSES_UPDATED:
    case ELEMENTS_VERIFIED_UPDATED:
    case EXPORTED_TO_BCF:
    case MINIMAL_PLANNED_BUILDING_ELEMENTS_LOADED:
    case PLANNED_BUILDING_ELEMENTS_LOADED:
    case PUSH_TO_BIM_UNDONE:
    case PUSHED_ALL_TO_BIM:
    case PUSHED_TO_BIM: {
      const payload = event.payload as { floorId?: string, firebaseFloorId?: string };
      const floorId = payload?.floorId || payload?.firebaseFloorId;
      if (floorId && !elementDetails.byFirebaseFloorId[floorId]) {
        return elementDetails;
      }
      return produce(elementDetails, (draft) => {
        if (floorId) {
          delete draft.byFirebaseFloorId[floorId];
        } else {
          draft.byFirebaseFloorId = {};
        }
      });
    }
    default: {
      return elementDetails;
    }
  }
};

export default reduceElementDetails;
